define([
  'react',
  'prop-types',
  'immutable',
  'underscore',
  'countdown',
  'es6!sumo/util',
  'jsx!launcher/views/listbuilder/wysiwyg/components/Component',
  'jsx!launcher/views/listbuilder/wysiwyg/components/Countdowns/CustomCountdown',
], (React, PropTypes, Immutable, _, countdown, Util, Component, defaultCustomCountdown) => {
  const VALID_STYLE_ATTRS = [
    'color',
    'background',
    'backgroundColor',
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'fontSize',
    'textAlign',
    'borderTopWidth',
    'borderLeftWidth',
    'borderBottomWidth',
    'borderRightWidth',
    'borderColor',
    'borderStyle',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'lineHeight',
    'boxShadow',
    'textShadow',
  ];

  const CustomCountdown = defaultCustomCountdown.default;

  const {
    getCookie,
    setCookie,
    EVERGREEN_COOKIE_KEY,
    COUNTDOWN_STYLES,
    COUNTDOWN_TYPES,
    COUNTDOWN_TYPE_ENDS_OPTIONS,
  } = Util;

  /**
  * Generates a unique key for the countdown based on the popup ID.
  * This is useful for evergreen timers to ensure they are unique per form.
  * @param {string} params.popupId - The ID of the popup.
  * @returns {string} - The unique key for the countdown.
  */
  const getFormCookieKey = ({ popupId }) => (`${EVERGREEN_COOKIE_KEY}-${popupId}`);

  /**
    * return endDate based on:
    * - timerType === 'evergreen': pull from or write to a cookie named `evergreen_${id}`
    * - otherwise: use the ISO string props.end
    * @param {string} timerType - The type of the countdown timer.
    * @param {number} timerDuration - The duration of the countdown timer.
    * @param {string} timerDurationOption - The unit of the timer duration.
    * @param {string} end - The end date in ISO string format.
    * @returns {Date} - The computed end date for the countdown.
    */
  const getComputedEndDate = ({
    popupId,
    timerType,
    timerDuration,
    timerDurationOption,
    end,
  }) => {
    if (timerType === COUNTDOWN_TYPES.EVERGREEN) {
      // 1) single cookie for first-visit
      let firstVisit = getCookie(getFormCookieKey({ popupId }));

      if (!firstVisit) {
        // if no cookie, set it to now
        // this will be used to compute the end date
        firstVisit = Date.now();
        setCookie(getFormCookieKey({ popupId }), firstVisit);
      }

      // 2) compute end based on that
      const unitToMs = {
        seconds: 1000,
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
      };
      const msPerUnit = unitToMs[timerDurationOption] || 0;
      const newDate = new Date(Number(firstVisit) + timerDuration * msPerUnit);
      return newDate;
    }

    // classic fixed-date
    return new Date(end);
  };

  /**
   * Check if the countdown is finished and handle an action.
   * If the countdown is finished and the action is available and this is not a preview,
   * it will call the onClickAction with the action set in the form.
   * @param {Object} props - The properties of the Countdown component.
   * @param {Function} props.onClickAction - The action to perform when the countdown ends.
   * @param {string} props.whenTimerEnds - The action to take when the timer ends.
   * @param {boolean} props.isPreview - Whether the countdown is in preview mode.
   */
  const checkActionOnTimerFinished = (props) => {
    const {
      popupId,
      timerType,
      onClickAction,
      whenTimerEnds,
      whenTimerEndsAction,
    } = props;

    const endDate = getComputedEndDate(props);
    const timeRemaining = countdown(new Date(), endDate);
    const { value = 0 } = timeRemaining;

    // first, we check if the countdown is already finished
    // and this is not a preview,
    if (value <= 0) {
      // if the countdown is finished and the action is available
      // we can perform the action
      if (whenTimerEnds === COUNTDOWN_TYPE_ENDS_OPTIONS.STOP) {
        onClickAction('close');
        return true;
      }

      // if the action is to redirect, we can navigate to the URL
      // to perform the redirection
      if (whenTimerEnds === COUNTDOWN_TYPE_ENDS_OPTIONS.REDIRECT) {
        window.location.href = whenTimerEndsAction;
        return true;
      }

      if (
        whenTimerEnds === COUNTDOWN_TYPE_ENDS_OPTIONS.RESTART
        && timerType === COUNTDOWN_TYPES.EVERGREEN
      ) {
        // if the action is restart, and the timer type is evergreen,
        // we can reset the cookie to now, so the countdown will restart
        setCookie(getFormCookieKey({ popupId }), Date.now());
        return false;
      }

      return false;
    }

    return false;
  };

  @Component({ disableVerticalResize: true })
  class Countdown extends React.Component {
    static propTypes = {
      id: PropTypes.string.isRequired,
      end: PropTypes.string,
      format: PropTypes.string,
      style: PropTypes.object,
      onMouseEnter: PropTypes.func,
      onMouseLeave: PropTypes.func,
    };

    static defaultProps = {
      style: Immutable.Map(),
      onMouseEnter: () => {},
      onMouseLeave: () => {},
    }

    static getUserCookie = getCookie;

    static setUserCookie = setCookie;

    static getComputedEndDate = getComputedEndDate;

    static checkActionOnTimerFinished = checkActionOnTimerFinished;

    constructor(props) {
      super(props);

      // figure out a single, fixed endDate up front
      const endDate = Countdown.getComputedEndDate(props);

      this.state = {
        timeRemaining: countdown(new Date(), endDate),
      };

      // Check if the countdown is finished and handle the end action
      if (!props.isPreview) {
        Countdown.checkActionOnTimerFinished(props);
      }

      this.updateTimeRemaining = this.updateTimeRemaining.bind(this);
    }

    componentWillReceiveProps(nextProps) {
      const endDate = Countdown.getComputedEndDate(nextProps);
      this.updateTimeRemaining(endDate);
    }

    componentDidMount() {
      this.timer = setInterval(() => {
        const endDate = Countdown.getComputedEndDate(this.props);
        this.updateTimeRemaining(endDate);
      }, 1000);
    }

    componentWillUnmount() {
      clearInterval(this.timer);
    }

    updateTimeRemaining(end) {
      const { timeRemaining } = this.state;
      const newTimeRemaining = countdown(new Date(), new Date(end));

      if (timeRemaining !== newTimeRemaining) {
        this.setState({ timeRemaining: newTimeRemaining });
      }
    }

    render() {
      const {
        isPreview,
        format,
        styleName,
        onMouseEnter,
        onMouseLeave,
        whenTimerEnds,
        whenTimerEndsAction,
      } = this.props;
      const { timeRemaining } = this.state;
      const { value } = timeRemaining;
      const style = _.pick(this.props.style.toJS(), VALID_STYLE_ATTRS);

      style.padding = '0';
      style.margin = '0';

      // Check if the countdown is finished and handle an action if available.
      // Mostly if the timer was active after the component was mounted
      if (!isPreview) {
        Countdown.checkActionOnTimerFinished(this.props);
      }

      if (value <= 0 && whenTimerEnds === COUNTDOWN_TYPE_ENDS_OPTIONS.MESSAGE) {
        // if the countdown is over and the action is to show a message
        const messageStyle = {
          ...style,
          fontSize: '24px',
          textAlign: 'center',
        };

        return (
          <div
            ref={(el) => { this.textEl = el; }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <p style={messageStyle}>{whenTimerEndsAction}</p>
          </div>
        );
      }

      if (styleName === COUNTDOWN_STYLES.classic.value) {
        return (
          <div
            ref={(el) => { this.textEl = el; }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <p style={style}>{Util.formatTime(timeRemaining, format)}</p>
          </div>
        );
      }

      return (
        <CustomCountdown
          style={style}
          styleName={styleName}
          ref={(el) => this.textEl = el}
          timeRemaining={timeRemaining}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      );
    }
  }

  // Assign static methods to the Countdown component
  // This is necessary because the Component decorator wraps the class and we need to ensure
  // that the static methods are available on the Countdown class.
  Object.assign(Countdown, {
    getFormCookieKey,
    getComputedEndDate,
    checkActionOnTimerFinished,
  });

  return Countdown;
});
